// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — the screens that are not the live game: shell + footer, menu, how-to,
 * about, and the results table. The play surface lives in play.ts.
 */

import { el, clear } from './dom';
import { MODES, type Mode } from './modes';
import { SYSTEM_NAME, type BreakdownRow, type Summary, type System } from './game';

const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export interface Shell {
  view: HTMLElement;
  footer: HTMLElement;
}

export function mountShell(root: HTMLElement): Shell {
  clear(root);
  const wrap = el('div', { class: 'shell' });
  const view = el('div', { class: 'main-content' });
  const footer = el('footer', { class: 'site-footer', html: ATTRIB });
  wrap.append(view, footer);
  root.append(wrap);
  return { view, footer };
}

// ── menu ─────────────────────────────────────────────────────────────────────

export interface MenuOpts {
  mode: Mode;
  best: number | null;
  muted: boolean;
  onMode: (m: Mode) => void;
  onSolo: () => void;
  onDaily: () => void;
  onFriends: () => void;
  onHelp: () => void;
  onAbout: () => void;
  onMute: (muted: boolean) => void;
}

export function renderMenu(view: HTMLElement, o: MenuOpts): void {
  clear(view);
  const s = el('div', { class: 'screen menu' });

  s.append(el('h1', { class: 'title', text: 'Boxbox' }));
  s.append(
    el('p', {
      class: 'tagline',
      text: 'Call the pit stop. Fix what’s broken, skip what isn’t, beat the clock.',
    }),
  );

  // mode selector
  const modes = el('div', { class: 'modes' });
  for (const m of MODES) {
    const active = m.id === o.mode.id;
    const b = el('button', {
      class: `mode-card${active ? ' active' : ''}`,
      type: 'button',
      'aria-pressed': active ? 'true' : 'false',
    });
    b.append(el('span', { class: 'mode-name', text: m.name }));
    b.append(el('span', { class: 'mode-blurb', text: m.blurb }));
    b.addEventListener('click', () => o.onMode(m));
    modes.append(b);
  }
  s.append(modes);

  if (o.best && o.best > 0) {
    s.append(el('p', { class: 'best', text: `Best ${o.mode.name}: ${o.best.toFixed(1)}s` }));
  }

  const play = el('div', { class: 'menu-actions' });
  const solo = el('button', { class: 'btn primary big', type: 'button' }, ['Play']);
  solo.addEventListener('click', o.onSolo);
  const daily = el('button', { class: 'btn', type: 'button' }, ["Today’s race"]);
  daily.addEventListener('click', o.onDaily);
  const friends = el('button', { class: 'btn', type: 'button' }, ['Play with friends']);
  friends.addEventListener('click', o.onFriends);
  play.append(solo, daily, friends);
  s.append(play);

  const minor = el('div', { class: 'menu-minor' });
  const help = el('button', { class: 'btn ghost', type: 'button' }, ['How to play']);
  help.addEventListener('click', o.onHelp);
  const about = el('button', { class: 'btn ghost', type: 'button' }, ['About']);
  about.addEventListener('click', o.onAbout);
  const mute = el('button', { class: 'btn ghost', type: 'button' }, [o.muted ? '🔇 Muted' : '🔊 Sound']);
  mute.addEventListener('click', () => o.onMute(!o.muted));
  minor.append(help, about, mute);
  s.append(minor);

  view.append(s);
}

// ── how to play ──────────────────────────────────────────────────────────────

export function renderHelp(view: HTMLElement, back: () => void): void {
  clear(view);
  const s = el('div', { class: 'screen prose' });
  s.append(el('h2', { text: 'How to play' }));
  s.append(
    el('p', {
      text: 'You are the pit wall. Each stop, the car comes in with four systems — tyres, fuel, front wing, brakes — and you decide which the crew services.',
    }),
  );
  const ul = el('ul', {});
  for (const line of [
    'Read the telemetry bars and the driver’s radio. The radio is subjective and sometimes plain wrong — weigh it against the data.',
    'Every job you do costs seconds in the stop. Every fault you leave costs seconds on track — and a system sent out CRITICAL blows up (puncture, brake failure, running dry) for a big penalty.',
    'A full service is never the right call, and neither is the bare minimum. Triage.',
    'Then run the stop: jack up before the wheels come off, wheels on before you drop the jack, and hit GO only when it’s all clear. An unsafe release is a penalty, not a loss.',
    'Lowest TOTAL time over the race wins. The results show the perfect line on your exact seed.',
  ]) {
    ul.append(el('li', { text: line }));
  }
  s.append(ul);
  s.append(
    el('p', {
      class: 'dim',
      text: 'Sensors go dark and drivers panic more in Monsoon — that is when the radio earns its keep.',
    }),
  );
  const b = el('button', { class: 'btn primary', type: 'button' }, ['Back']);
  b.addEventListener('click', back);
  s.append(b);
  view.append(s);
}

// ── about ────────────────────────────────────────────────────────────────────

export function renderAbout(view: HTMLElement, back: () => void): void {
  clear(view);
  const s = el('div', { class: 'screen prose' });
  s.append(el('h2', { text: 'About Boxbox' }));
  s.append(
    el('p', {
      text: 'A game of pit-stop calls. Original — no real series, team, driver or circuit. All art and audio are procedural.',
    }),
  );
  s.append(
    el('p', {
      text: 'Play with friends is peer-to-peer: everyone races the identical seed and compares totals. It uses a public signalling relay only to connect players (which exposes your IP to the people you play with, as any video call does); no game data is ever stored on a server, and there are no cookies or trackers beyond an anonymous Cloudflare page-view count.',
    }),
  );
  const b = el('button', { class: 'btn primary', type: 'button' }, ['Back']);
  b.addEventListener('click', back);
  s.append(b);
  view.append(s);
}

// ── results ──────────────────────────────────────────────────────────────────

export interface ResultEntry {
  name: string;
  isSelf: boolean;
  total: number;
  catastrophes: number;
  done: boolean;
  gone: boolean;
}

export interface ResultsOpts {
  mode: Mode;
  summary: Summary;
  best: number | null;
  entries: ResultEntry[];
  multiplayer: boolean;
  closesInMs: number | null;
  votes: string;
  startsInMs: number | null;
  canForceStart: boolean;
  tally: string;
  onAgain: () => void;
  onLobby: () => void;
  onMenu: () => void;
  onShare: () => void;
  onForceStart: () => void;
  onFeedback: (from: HTMLElement) => void;
}

export function renderResults(view: HTMLElement, o: ResultsOpts): void {
  clear(view);
  const s = el('div', { class: 'screen results' });

  const rank = o.entries.filter((e) => e.done);
  const myIndex = o.entries.findIndex((e) => e.isSelf);
  const place = myIndex >= 0 ? myIndex + 1 : 0;
  const finishers = o.entries.length;

  s.append(el('h2', { text: 'Chequered flag' }));
  if (o.multiplayer && finishers > 1) {
    s.append(el('p', { class: 'place', text: `P${place} of ${finishers}` }));
  }
  s.append(
    el('p', {
      class: 'result-total',
      html: `Your race: <strong>${o.summary.total.toFixed(1)}s</strong> · perfect line ${o.summary.optimal.toFixed(1)}s · <span class="lost">+${o.summary.lost.toFixed(1)}s left on the table</span>`,
    }),
  );
  if (o.best && o.best > 0) {
    s.append(el('p', { class: 'dim', text: `Best ${o.mode.name}: ${o.best.toFixed(1)}s` }));
  }
  if (o.tally) s.append(el('p', { class: 'tally', text: o.tally }));

  // Everyone's result (principle #9).
  const table = el('div', { class: 'ladder' });
  o.entries.forEach((e, i) => {
    const row = el('div', { class: `ladder-row${e.isSelf ? ' me' : ''}${e.gone ? ' gone' : ''}` });
    row.append(el('span', { class: 'ladder-pos', text: e.done ? `${i + 1}` : '—' }));
    row.append(el('span', { class: 'ladder-name', text: e.name }));
    row.append(
      el('span', {
        class: 'ladder-time',
        text: e.gone ? 'left' : e.done ? `${e.total.toFixed(1)}s` : 'racing…',
      }),
    );
    row.append(
      el('span', {
        class: 'ladder-cata',
        text: e.done && e.catastrophes > 0 ? `${e.catastrophes} DNF-scare` : '',
      }),
    );
    table.append(row);
  });
  s.append(table);
  void rank;

  // Your stop-by-stop calls vs the perfect line (what you missed).
  s.append(el('h3', { class: 'breakdown-h', text: 'Your calls vs the perfect line' }));
  const bd = el('div', { class: 'breakdown' });
  for (const r of o.summary.rows) {
    bd.append(breakdownRow(r));
  }
  s.append(bd);

  // waiting / rematch state
  if (o.multiplayer) {
    const wait = el('div', { class: 'wait' });
    if (o.closesInMs !== null) {
      wait.append(
        el('p', { class: 'dim', text: `Waiting for the field… ladder locks in ${sec(o.closesInMs)}s` }),
      );
    }
    if (o.votes) wait.append(el('p', { class: 'dim', text: o.votes }));
    if (o.startsInMs !== null) {
      wait.append(el('p', { class: 'go-soon', text: `Next race starts in ${sec(o.startsInMs)}s…` }));
    }
    s.append(wait);
  }

  const actions = el('div', { class: 'results-actions' });
  const again = el('button', { class: 'btn primary big', type: 'button' }, [
    o.multiplayer ? 'Play again' : 'Race again',
  ]);
  again.addEventListener('click', o.onAgain);
  actions.append(again);
  if (o.multiplayer) {
    if (o.canForceStart) {
      const force = el('button', { class: 'btn', type: 'button' }, ['Force start']);
      force.addEventListener('click', o.onForceStart);
      actions.append(force);
    }
    const lobby = el('button', { class: 'btn', type: 'button' }, ['Back to lobby']);
    lobby.addEventListener('click', o.onLobby);
    actions.append(lobby);
  }
  const share = el('button', { class: 'btn', type: 'button' }, ['Share']);
  share.addEventListener('click', o.onShare);
  actions.append(share);
  const menu = el('button', { class: 'btn ghost', type: 'button' }, ['Menu']);
  menu.addEventListener('click', o.onMenu);
  actions.append(menu);
  s.append(actions);

  const fb = el('button', { class: 'btn ghost results-feedback', type: 'button' }, ['Feedback']);
  fb.addEventListener('click', () => o.onFeedback(fb));
  s.append(fb);

  view.append(s);
}

function breakdownRow(r: BreakdownRow): HTMLElement {
  const row = el('div', { class: 'bd-row' });
  row.append(el('span', { class: 'bd-stop', text: `${r.stopIndex + 1}` }));
  const you = r.fixed.length ? r.fixed.map(shortSys).join(' ') : '—';
  const opt = r.optimalFix.length ? r.optimalFix.map(shortSys).join(' ') : '—';
  const match = sameSet(r.fixed, r.optimalFix);
  row.append(el('span', { class: `bd-you${match ? ' ok' : ' off'}`, text: you }));
  row.append(el('span', { class: 'bd-arrow', text: match ? '=' : '≠' }));
  row.append(el('span', { class: 'bd-opt', text: opt }));
  const lost = r.penalty;
  row.append(
    el('span', {
      class: 'bd-lost',
      text: r.catastrophes.length ? `💥 +${(r.stopTime + r.penalty).toFixed(1)}s` : `${lost.toFixed(1)}s`,
    }),
  );
  return row;
}

function shortSys(s: System): string {
  return { tyres: 'TYR', fuel: 'FUE', wing: 'WNG', brakes: 'BRK' }[s] ?? SYSTEM_NAME[s];
}

function sameSet(a: System[], b: System[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

function sec(ms: number): string {
  return Math.ceil(ms / 1000).toString();
}
